<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - General</template>

                <template #actions>
                    <jet-button type="submit" class="h-7 leading-none" :disabled="form.processing" >
                        Save
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="px-8 grid-cols-4">
                <div class="w-80 form-group">
                    <jet-label value="Auto-print Enabled" />
                    <div>
                        <toggle class="block" id="autoprint_enabled" v-model="form.autoprint_enabled" />
                        <div class="text-xs text-gray-500">Refresh pages you wish to print from with the new method after updating this setting.</div>
                    </div>
                </div>

                <div class="w-80 form-group">
                    <jet-label value="Wholesale Delivery Order Minimum" />
                    <div class="flex">
                        <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                        <jet-input type="number" v-model="form.wholesale_delivery_order_minimum" class="w-full rounded-l-none" />
                    </div>
                </div>

                <div class="w-80 form-group">
                    <jet-label value="Wholesale Delivery Fee Below Minimum" />
                    <div class="flex">
                        <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                        <jet-input type="number" v-model="form.wholesale_delivery_fee_below_minimum" class="w-full rounded-l-none" />
                    </div>
                    <div class="text-xs text-gray-500">Leave blank to refuse orders below the order minimum.</div>
                </div>
                
                <div class="w-80 form-group">
                    <jet-label value="Number of delivery days" />
                    <multiselect
                        v-model="form.number_of_delivery_days"
                        :options="numberDays"
                        :classes="{ singleLabel: 'w-full px-2' }"
                        class="multi-lg h-7 text-base"
                        placeholder="Select number"
                    />
                    <jet-input-error :message="$page.props.errors.type" class="mt-1" />
                </div>

                <div class="w-80 form-group">
                    <jet-label value="Evening delivery cut off at" />
                    <jet-input type="time" v-model="form.delivery_past_evening_cut_off" class="w-full" min="1" />
                </div>

                <div class="w-80 form-group">
                    <jet-label value="Pickup Early Afternoon (12-3) Cutoff" />
                    <jet-input type="time" v-model="form.pickup_afternoon_cutoff" class="w-full" min="1" />
                </div>

                <!-- <div class="w-80 form-group">
                    <jet-label value="Pickup Late Afternoon (3-5) Cutoff" />
                    <jet-input type="time" v-model="form.pickup_late_afternoon_cutoff" class="w-full" min="1" />
                </div> -->

                <div class="w-80 form-group">
                    <jet-label value="Pickup Late Afternoon / Evening (3-7) Cutoff" />
                    <jet-input type="time" v-model="form.pickup_evening_cutoff" class="w-full" min="1" />
                </div>

                <div class="w-80 form-group">
                    <jet-label value="OptimoRoute Skills" />
                    <jet-input type="text" v-model="form.optimoroute_skills" class="w-full" placeholder="SCH, WHO, Retail, EVE" />
                    <div class="text-xs text-gray-500 mt-1">Comma-separated codes shown as options on customers and orders. Must also exist in OptimoRoute Administration → Skills.</div>
                    <div v-if="missingOptimoRouteSkills.length" class="text-xs text-red-600 mt-1">
                        OptimoRoute does not recognize: {{ missingOptimoRouteSkills.join(', ') }}. Add these under Administration → Skills (or remove them here).
                    </div>
                    <jet-input-error :message="$page.props.errors.optimoroute_skills" class="mt-1" />
                </div>

                <div class="w-80 form-group">
                    <jet-label value="OptimoRoute Vehicle Types" />
                    <jet-input type="text" v-model="form.optimoroute_vehicle_types" class="w-full" placeholder="BOXTRUCK" />
                    <div class="text-xs text-gray-500 mt-1">Comma-separated codes shown as options on customers and orders. Must also exist in OptimoRoute Administration → Vehicle features.</div>
                    <div v-if="missingOptimoRouteVehicleTypes.length" class="text-xs text-red-600 mt-1">
                        OptimoRoute does not recognize: {{ missingOptimoRouteVehicleTypes.join(', ') }}. Add these under Administration → Vehicle features (or remove them here).
                    </div>
                    <jet-input-error :message="$page.props.errors.optimoroute_vehicle_types" class="mt-1" />
                </div>
            </div>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetButton from '@/Jetstream/Button';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

function settingValue(settings, key, fallback = '') {
    const row = settings.find(item => item.label === key);
    return row && row.value != null ? row.value : fallback;
}

export default {
    setup(props) {
        const form = useForm({
            autoprint_enabled: settingValue(props.settings, 'autoprint_enabled'),
            wholesale_delivery_order_minimum: settingValue(props.settings, 'wholesale_delivery_order_minimum'),
            wholesale_delivery_fee_below_minimum: settingValue(props.settings, 'wholesale_delivery_fee_below_minimum'),
            number_of_delivery_days: settingValue(props.settings, 'number_of_delivery_days'),
            delivery_past_evening_cut_off: settingValue(props.settings, 'delivery_past_evening_cut_off'),
            pickup_afternoon_cutoff: settingValue(props.settings, 'pickup_afternoon_cutoff'),
            pickup_evening_cutoff: settingValue(props.settings, 'pickup_evening_cutoff'),
            optimoroute_skills: settingValue(props.settings, 'optimoroute_skills', 'SCH, WHO, Retail, EVE'),
            optimoroute_vehicle_types: settingValue(props.settings, 'optimoroute_vehicle_types', 'BOXTRUCK'),
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        SettingsNavigation,
        Multiselect,
        Toggle,
    },

    props: {
        settings: { type: Array, required: true },
        numberDays: { type: Array, required: true },
        missingOptimoRouteSkills: { type: Array, default: () => [] },
        missingOptimoRouteVehicleTypes: { type: Array, default: () => [] },
    },

    methods: {
        submit() {
            this.form.submit('post', route('settings.general.store'));
        },
    },
}
</script>

<style scoped>
</style>
